import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './UploadPage.css';

const LARAVEL_API_URL = window.location.origin + '/auth-api';
axios.defaults.withCredentials = true;

function UploadPage() {
  const navigate = useNavigate();
  const [dataType, setDataType] = useState('file');
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [url, setUrl] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [documents, setDocuments] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCommon, setIsCommon] = useState(false);

  useEffect(() => {
    loadUser();
    loadDocuments();
  }, []);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${LARAVEL_API_URL}/user`);
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      console.error('ユーザー情報の取得エラー:', error);
      setIsAdmin(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`${LARAVEL_API_URL}/documents`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('ドキュメント一覧の読み込みエラー:', error);
      if (error.response?.status === 401) {
        window.location.href = '/auth/login';
      }
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    if (selectedFile && selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setImagePreview(null);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setMessage('ファイルを選択してください');
      return;
    }

    setUploading(true);
    setMessage('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('data_type', 'file');
    formData.append('title', title);
    formData.append('comment', comment);
    if (isCommon) formData.append('is_common', '1');

    try {
      const response = await axios.post(`${LARAVEL_API_URL}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessage(response.data.message);
      setFile(null);
      setTitle('');
      setComment('');
      setImagePreview(null);
      setIsCommon(false);
      loadDocuments();
    } catch (error) {
      setMessage(error.response?.data?.error || 'アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleWebUpload = async (e) => {
    e.preventDefault();
    if (!url) {
      setMessage('URLを入力してください');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const response = await axios.post(`${LARAVEL_API_URL}/documents`, {
        data_type: 'web',
        url,
        title,
        comment,
        is_common: isCommon ? 1 : 0,
      });
      setMessage(response.data.message);
      setUrl('');
      setTitle('');
      setComment('');
      setIsCommon(false);
      loadDocuments();
    } catch (error) {
      setMessage(error.response?.data?.error || 'アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleTextUpload = async (e) => {
    e.preventDefault();
    if (!question || !answer) {
      setMessage('質問と回答の両方を入力してください');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const response = await axios.post(`${LARAVEL_API_URL}/documents`, {
        data_type: 'text',
        title,
        question,
        answer,
        is_common: isCommon ? 1 : 0,
      });
      setMessage(response.data.message);
      setTitle('');
      setQuestion('');
      setAnswer('');
      setIsCommon(false);
      loadDocuments();
    } catch (error) {
      setMessage(error.response?.data?.error || 'アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('このドキュメントを削除しますか？')) return;

    try {
      await axios.delete(`${LARAVEL_API_URL}/documents/${docId}`);
      setMessage('ドキュメントを削除しました');
      loadDocuments();
    } catch (error) {
      setMessage('削除に失敗しました');
    }
  };

  const handleToggleCommon = async (docId, isCurrentlyCommon) => {
    const confirmMsg = isCurrentlyCommon
      ? 'このドキュメントを個人知識に変更しますか？'
      : 'このドキュメントを共通知識に変更しますか？（全ユーザーが閲覧可能になります）';

    if (!window.confirm(confirmMsg)) return;

    try {
      const response = await axios.put(`${LARAVEL_API_URL}/documents/${docId}/toggle-common`);
      setMessage(response.data.message);
      loadDocuments();
    } catch (error) {
      setMessage(error.response?.data?.error || '変更に失敗しました');
    }
  };

  const personalDocs = documents.filter((doc) => !doc.is_common);
  const commonDocs = documents.filter((doc) => doc.is_common);
  const isErrorMessage = message.includes('失敗') || message.includes('エラー');

  const commonCheckbox = (id) =>
    isAdmin ? (
      <label className="check-row" htmlFor={id}>
        <input
          type="checkbox"
          id={id}
          checked={isCommon}
          onChange={(e) => setIsCommon(e.target.checked)}
        />
        <span>共通知識として保存（全ユーザーが利用可能）</span>
      </label>
    ) : null;

  const renderDocCard = (doc, common) => (
    <article key={doc.id} className="doc-item">
      <div className="doc-top">
        <h3>{doc.title || '無題'}</h3>
        <span className="type-pill">{doc.file_type}</span>
      </div>
      {doc.comment && <p className="doc-comment">{doc.comment}</p>}
      <div className="doc-meta">
        <span>{doc.chunks_count} チャンク</span>
        <span>{new Date(doc.created_at).toLocaleDateString('ja-JP')}</span>
      </div>
      <div className="doc-actions">
        {isAdmin && (
          <button
            type="button"
            className="btn-ghost"
            onClick={() => handleToggleCommon(doc.id, common)}
          >
            {common ? '個人知識へ' : '共通知識へ'}
          </button>
        )}
        {(isAdmin || !common) && (
          <button type="button" className="btn-danger" onClick={() => handleDelete(doc.id)}>
            削除
          </button>
        )}
      </div>
    </article>
  );

  return (
    <div className="upload-page">
      <header className="page-header">
        <div className="page-header-inner">
          <div className="page-header-left">
            <button type="button" className="nav-ghost" onClick={() => navigate('/')}>
              ← ホーム
            </button>
            <div className="page-brand">
              <div className="page-brand-name">MOMIBO</div>
              <div className="page-brand-sub">ドキュメント管理</div>
            </div>
          </div>
          <div className="page-header-right">
            {isAdmin && <span className="admin-pill">管理者</span>}
            <a className="nav-primary" href="/auth/dashboard">
              本部ホーム
            </a>
          </div>
        </div>
      </header>

      <main className="page-shell">
        <section className="page-panel">
          <p className="section-label">Upload</p>
          <h1 className="page-title">ドキュメント管理</h1>
          <p className="page-lead">ファイル・Web・テキストで知識を追加できます。</p>

          {message && (
            <div className={`message-alert ${isErrorMessage ? 'is-error' : 'is-ok'}`}>
              {message}
            </div>
          )}

          <div className="type-tabs" role="tablist">
            {[
              { value: 'file', label: 'ファイル' },
              { value: 'web', label: 'Web' },
              { value: 'text', label: 'テキスト' },
            ].map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={dataType === tab.value}
                className={`type-tab ${dataType === tab.value ? 'is-active' : ''}`}
                onClick={() => setDataType(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {dataType === 'file' && (
            <form onSubmit={handleFileUpload} className="upload-form">
              <label className="field">
                <span>ファイル選択（PDF / Word / Excel / 画像 など）</span>
                <input
                  type="file"
                  accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.txt,.png,.jpg,.jpeg,.gif,.bmp,.webp"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
              </label>
              {imagePreview && (
                <div className="preview-box">
                  <img src={imagePreview} alt="プレビュー" />
                </div>
              )}
              <label className="field">
                <span>タイトル</span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={uploading}
                  placeholder="わかりやすい名前"
                />
              </label>
              <label className="field">
                <span>コメント</span>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows="3"
                  disabled={uploading}
                  placeholder="補足があれば入力"
                />
              </label>
              {commonCheckbox('file-common')}
              <button type="submit" className="nav-primary submit-btn" disabled={uploading || !file}>
                {uploading ? 'アップロード中…' : '送信'}
              </button>
            </form>
          )}

          {dataType === 'web' && (
            <form onSubmit={handleWebUpload} className="upload-form">
              <label className="field">
                <span>URL</span>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  disabled={uploading}
                />
              </label>
              <label className="field">
                <span>タイトル</span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={uploading}
                />
              </label>
              <label className="field">
                <span>コメント</span>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows="3"
                  disabled={uploading}
                />
              </label>
              {commonCheckbox('web-common')}
              <button type="submit" className="nav-primary submit-btn" disabled={uploading || !url}>
                {uploading ? 'アップロード中…' : '送信'}
              </button>
            </form>
          )}

          {dataType === 'text' && (
            <form onSubmit={handleTextUpload} className="upload-form">
              <label className="field">
                <span>タイトル</span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={uploading}
                />
              </label>
              <label className="field">
                <span>質問</span>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows="3"
                  disabled={uploading}
                />
              </label>
              <label className="field">
                <span>回答</span>
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  rows="3"
                  disabled={uploading}
                />
              </label>
              {commonCheckbox('text-common')}
              <button
                type="submit"
                className="nav-primary submit-btn"
                disabled={uploading || !question || !answer}
              >
                {uploading ? 'アップロード中…' : '送信'}
              </button>
            </form>
          )}
        </section>

        <section className="page-panel">
          <p className="section-label">Personal</p>
          <h2 className="page-title sm">個人の知識</h2>
          <p className="page-lead">あなただけが使えるドキュメントです。</p>
          {personalDocs.length === 0 ? (
            <div className="state-box">まだ個人知識はありません</div>
          ) : (
            <div className="doc-grid">
              {personalDocs.map((doc) => renderDocCard(doc, false))}
            </div>
          )}
        </section>

        <section className="page-panel">
          <p className="section-label">Shared</p>
          <h2 className="page-title sm">共通知識</h2>
          <p className="page-lead">全ユーザーが利用できるドキュメントです。</p>
          {commonDocs.length === 0 ? (
            <div className="state-box">まだ共通知識はありません</div>
          ) : (
            <div className="doc-grid">
              {commonDocs.map((doc) => renderDocCard(doc, true))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default UploadPage;
